import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../components/ui/button';
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormControl,
	FormLabel,
	Radio,
	RadioGroup,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	CircularProgress,
	Autocomplete,
	Alert,
	AlertTitle,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import * as Yup from 'yup';
import styles from './CreatePartnerSettings.module.scss';
import { CommonPartner } from 'types';
import {
	getCommonPartners,
	createPartner,
	getPartnerTypes,
	PartnerType,
	createPartnerListWorkplace,
	CreatePartnerPayload,
} from 'core/API/partners';
import { GetAllWorkplaceList } from 'core/API/workplaces';
import useSettingsStore from 'store/useSettingsStore';
import { useGoogleAuth } from './useGoogleAuth';
import {
	useOneDriveAuth,
	OneDriveDrive,
	OneDriveRootItem,
	OneDriveSourceType,
} from './useOneDriveAuth';
import ToastConnectFlow, { ToastPartnerParameters } from './ToastConnectFlow';

interface CreatePartnerSettingsProps {
	backButtonClick: () => void;
	onUserCreated?: () => void;
}

interface Workplace {
	id: number;
	name: string;
	level: number;
	selected: boolean;
	'@id'?: string;
}

const CreatePartnerSettings: React.FC<CreatePartnerSettingsProps> = ({
	backButtonClick,
	onUserCreated,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const addPartner = useSettingsStore((s) => s.addPartner);
	const [activateDialogOpen, setActivateDialogOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [workplaceUnassignDialogOpen, setWorkplaceUnassignDialogOpen] = useState(false);
	const [pendingWorkplaceId, setPendingWorkplaceId] = useState<number | null>(null);
	const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
	const [loadingWorkplaces, setLoadingWorkplaces] = useState(false);
	const [commonPartners, setCommonPartners] = useState<CommonPartner[]>([]);
	const [loadingCommonPartners, setLoadingCommonPartners] = useState(false);
	const [partnerTypes, setPartnerTypes] = useState<PartnerType[]>([]);
	const [loadingPartnerTypes, setLoadingPartnerTypes] = useState(false);
	const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
	const [expandedDriveIds, setExpandedDriveIds] = useState<Record<string, boolean>>({});
	const [toastFlowKey, setToastFlowKey] = useState(0);
	const [isToastRestaurantConnected, setIsToastRestaurantConnected] = useState(false);

	// Google Drive OAuth integration
	const {
		accessToken,
		encryptedRefreshToken: googleEncryptedRefreshToken,
		sharedDrives,
		selectedDriveId,
		isConnected: isGoogleConnected,
		isLoading: isGoogleAuthLoading,
		error: googleAuthError,
		initiateGoogleAuth,
		resetAuth: resetGoogleAuth,
		setSelectedDriveId,
	} = useGoogleAuth();

	// Microsoft OneDrive OAuth integration
	const {
		accessToken: oneDriveAccessToken,
		refreshToken: oneDriveRefreshToken,
		personalDrives,
		sharePointDrives,
		selectedSource: oneDriveSelectedSource,
		selectedLocation: oneDriveSelectedLocation,
		isConnected: isOneDriveConnected,
		isLoading: isOneDriveAuthLoading,
		error: oneDriveAuthError,
		initiateOneDriveAuth,
		resetAuth: resetOneDriveAuth,
		setSelectedSource: setOneDriveSelectedSource,
		selectDriveRoot: selectOneDriveRoot,
		selectDriveFolder: selectOneDriveFolder,
		buildPartnerParameters: buildOneDrivePartnerParameters,
	} = useOneDriveAuth();

	// Fetch common partners, partner types, and workplaces on mount
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoadingCommonPartners(true);
				setLoadingPartnerTypes(true);
				setLoadingWorkplaces(true);

				const [partners, types, workplacesData] = await Promise.all([
					getCommonPartners(),
					getPartnerTypes(),
					GetAllWorkplaceList(),
				]);
				console.log('workplaces', workplacesData);
				setCommonPartners(
					partners.filter((p) => {
						if (!p.available) return false;
						const label = p.label?.toLowerCase() || '';
						return (
							label.includes('google drive') ||
							label.includes('onedrive') ||
							label.includes('one drive') ||
							label.includes('toast')
						);
					})
				);
				setPartnerTypes(types);
				setWorkplaces(
					(Object.values(workplacesData) || []).map((w) => ({
						id: w.id,
						name: w.name,
						level: 1, // You can enhance this with hierarchy logic if needed
						selected: false,
						'@id': `/api/workplaces/${w.id}`,
					}))
				);
			} catch (error: any) {
				console.error('Failed to fetch data:', error);
				setApiError('Failed to load options');
			} finally {
				setLoadingCommonPartners(false);
				setLoadingPartnerTypes(false);
				setLoadingWorkplaces(false);
			}
		};
		fetchData();
	}, []);

	// Auto-fill Google Drive parameters from OAuth / partner attempt only (no template keys)
	useEffect(() => {
		if (!accessToken || !isGoogleConnected) return;

		const next: Record<string, string> = {
			encrypted_refresh_token: googleEncryptedRefreshToken || accessToken,
		};
		if (selectedDriveId) {
			next.shared_drive_id = selectedDriveId;
		}
		setParameterValues(next);
	}, [accessToken, googleEncryptedRefreshToken, selectedDriveId, isGoogleConnected]);

	// Auto-fill OneDrive parameters from partner attempt + folder selection only (no template keys)
	useEffect(() => {
		if (!isOneDriveConnected) return;

		const next: Record<string, string> = {};

		if (oneDriveRefreshToken || oneDriveAccessToken) {
			next.encrypted_refresh_token = oneDriveRefreshToken || oneDriveAccessToken || '';
		}

		if (oneDriveSelectedLocation) {
			next.source = oneDriveSelectedLocation.source;
			next.drive_id = oneDriveSelectedLocation.drive_id;
			next.drive_name = oneDriveSelectedLocation.drive_name;
			next.folder_id = oneDriveSelectedLocation.folder_id ?? '';
			next.folder_name = oneDriveSelectedLocation.folder_name ?? '';

			if (oneDriveSelectedLocation.source === 'sharepoint_site') {
				next.site_id = oneDriveSelectedLocation.site_id ?? '';
				next.site_name = oneDriveSelectedLocation.site_name ?? '';
			}
		}

		setParameterValues(next);
	}, [
		oneDriveAccessToken,
		oneDriveRefreshToken,
		oneDriveSelectedLocation,
		isOneDriveConnected,
	]);

	const initialFormikValues = {
		label: '',
		partnerId: '',
		partnerTypeId: '',
		active: true,
	};

	const validationSchema = Yup.object().shape({
		label: Yup.string().required('Partner name is required'),
		partnerId: Yup.string().required('Partner is required'),
		partnerTypeId: Yup.string().required('Partner type is required'),
		active: Yup.boolean(),
	});

	const formik = useFormik({
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				const selectedPartner = commonPartners.find((p) => p.id === Number(values.partnerId));
				const selectedPartnerType = partnerTypes.find(
					(pt) => pt.id === Number(values.partnerTypeId)
				);

				if (!selectedPartner) {
					setApiError('Please select a partner');
					return;
				}

				if (!selectedPartnerType) {
					setApiError('Please select a partner type');
					return;
				}

				// Check if Google Drive is selected but not connected
				const isGoogleDrive =
					selectedPartner.label?.toLowerCase().includes('google drive') ||
					selectedPartnerType.label?.toLowerCase().includes('google drive');

				if (isGoogleDrive && !isGoogleConnected) {
					setApiError('Please connect to Google Drive before saving');
					return;
				}

				// Check if OneDrive is selected but not connected
				const isOneDrive =
					selectedPartner.label?.toLowerCase().includes('onedrive') ||
					selectedPartner.label?.toLowerCase().includes('one drive') ||
					selectedPartnerType.label?.toLowerCase().includes('onedrive') ||
					selectedPartnerType.label?.toLowerCase().includes('one drive');

				if (isOneDrive && !isOneDriveConnected) {
					setApiError('Please connect to Microsoft OneDrive before saving');
					return;
				}

				if (isOneDrive && !oneDriveSelectedLocation) {
					setApiError('Please select a OneDrive or SharePoint folder for this integration');
					return;
				}

				const isToast =
					selectedPartner.label?.toLowerCase().includes('toast') ||
					selectedPartnerType.label?.toLowerCase().includes('toast');

				if (isToast && (!parameterValues.restaurant_guid || !parameterValues.location_name)) {
					setApiError('Please search and confirm a Toast restaurant location before saving');
					return;
				}

				// Prepare the payload for creating a new partner_list
				// Build parameters object with user-entered values (including OAuth values if applicable)
				let finalParameters: Record<string, unknown> =
					Object.keys(parameterValues).length > 0
						? { ...parameterValues }
						: Array.isArray(selectedPartner.parameters) && selectedPartner.parameters.length > 0
							? { ...selectedPartner.parameters[0] }
							: {};

				if (isOneDrive) {
					finalParameters = buildOneDrivePartnerParameters(finalParameters);
				}

				console.log('[CreatePartner] Is Google Drive:', isGoogleDrive);
				console.log('[CreatePartner] Is OneDrive:', isOneDrive);
				console.log('[CreatePartner] Is Toast:', isToast);
				console.log('[CreatePartner] Access Token:', accessToken ? 'present' : 'missing');
				console.log('[CreatePartner] Selected Drive ID:', selectedDriveId);
				console.log('[CreatePartner] OneDrive location:', oneDriveSelectedLocation);
				console.log('[CreatePartner] Final Parameters:', finalParameters);

				const payload: CreatePartnerPayload = {
					label: values.label,
					partnerId: selectedPartner.id,
					type: selectedPartnerType['@id'],
					parameters: finalParameters as Record<string, any>,
					active: values.active,
					deleted: false,
				};

				const createdPartner = await createPartner(payload);

				// Create workplace associations for selected workplaces
				const selectedWorkplaces = workplaces.filter((w) => w.selected);
				if (selectedWorkplaces.length > 0 && createdPartner['@id']) {
					await Promise.all(
						selectedWorkplaces.map((workplace) =>
							createPartnerListWorkplace({
								partnerList: createdPartner['@id'] || '',
								workplace: workplace['@id'] || `/api/workplaces/${workplace.id}`,
							})
						)
					);
				}

				addPartner(createdPartner);
				setApiSuccess('Partner created successfully!');

				onUserCreated?.();

				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to create partner');
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const handleActivateToggle = () => {
		if (!formik.values.active) {
			setActivateDialogOpen(true);
		} else {
			setDeactivateDialogOpen(true);
		}
	};

	const confirmActivatePartner = () => {
		formik.setFieldValue('active', true);
		setActivateDialogOpen(false);
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.level === 1 ? { ...workplace, selected: true } : workplace
			)
		);
		setApiSuccess('Partner activated successfully! Organization workplace automatically assigned.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const confirmDeactivatePartner = () => {
		formik.setFieldValue('active', false);
		setDeactivateDialogOpen(false);
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: false })));
		setApiSuccess('Partner deactivated successfully! All workplace assignments removed.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const handleWorkplaceToggle = (workplaceId: number) => {
		const workplace = workplaces.find((w) => w.id === workplaceId);
		if (workplace && workplace.selected) {
			setPendingWorkplaceId(workplaceId);
			setWorkplaceUnassignDialogOpen(true);
		} else {
			toggleWorkplaceAssignment(workplaceId);
		}
	};

	const toggleWorkplaceAssignment = (workplaceId: number) => {
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.id === workplaceId ? { ...workplace, selected: !workplace.selected } : workplace
			)
		);
	};

	const handleSelectAllWorkplaces = (checked: boolean) => {
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: checked })));
	};

	const allWorkplacesSelected = workplaces.length > 0 && workplaces.every((w) => w.selected);
	const someWorkplacesSelected = workplaces.some((w) => w.selected) && !allWorkplacesSelected;

	// Helper to check if Google Drive is selected
	const isGoogleDriveSelected = () => {
		const selectedPartner = commonPartners.find((p) => p.id === Number(formik.values.partnerId));
		const selectedPartnerType = partnerTypes.find(
			(pt) => pt.id === Number(formik.values.partnerTypeId)
		);

		return (
			selectedPartner?.label?.toLowerCase().includes('google drive') ||
			selectedPartnerType?.label?.toLowerCase().includes('google drive') ||
			false
		);
	};

	// Helper to check if OneDrive is selected
	const isOneDriveSelected = () => {
		const selectedPartner = commonPartners.find((p) => p.id === Number(formik.values.partnerId));
		const selectedPartnerType = partnerTypes.find(
			(pt) => pt.id === Number(formik.values.partnerTypeId)
		);

		return (
			selectedPartner?.label?.toLowerCase().includes('onedrive') ||
			selectedPartner?.label?.toLowerCase().includes('one drive') ||
			selectedPartnerType?.label?.toLowerCase().includes('onedrive') ||
			selectedPartnerType?.label?.toLowerCase().includes('one drive') ||
			false
		);
	};

	const isToastSelected = () => {
		const selectedPartner = commonPartners.find((p) => p.id === Number(formik.values.partnerId));
		const selectedPartnerType = partnerTypes.find(
			(pt) => pt.id === Number(formik.values.partnerTypeId)
		);

		return (
			selectedPartner?.label?.toLowerCase().includes('toast') ||
			selectedPartnerType?.label?.toLowerCase().includes('toast') ||
			false
		);
	};

	const handleToastParametersChange = (params: ToastPartnerParameters | null) => {
		if (!params) {
			setIsToastRestaurantConnected(false);
			setParameterValues({});
			return;
		}

		const next: Record<string, string> = {
			restaurant_guid: params.restaurant_guid,
			restaurant_name: params.restaurant_name,
			location_name: params.location_name,
			restaurant_address: params.restaurant_address,
		};
		if (params.management_group_guid) {
			next.management_group_guid = params.management_group_guid;
		}
		if (params.restaurant_city) {
			next.restaurant_city = params.restaurant_city;
		}
		if (params.restaurant_state) {
			next.restaurant_state = params.restaurant_state;
		}
		if (params.restaurant_zip_code) {
			next.restaurant_zip_code = params.restaurant_zip_code;
		}
		if (params.restaurant_timezone) {
			next.restaurant_timezone = params.restaurant_timezone;
		}

		setParameterValues(next);
		setIsToastRestaurantConnected(true);
	};

	const resetToastFlow = () => {
		setToastFlowKey((k) => k + 1);
		setIsToastRestaurantConnected(false);
		setParameterValues({});
	};

	/** Hide Partner Configuration Parameters until OAuth succeeds for drive partners */
	const shouldShowParameterFields = () => {
		if (Object.keys(parameterValues).length === 0) {
			return false;
		}
		if (isGoogleDriveSelected()) {
			return isGoogleConnected;
		}
		if (isOneDriveSelected()) {
			return isOneDriveConnected;
		}
		// Toast params are shown in the Toast connect confirmation UI
		if (isToastSelected()) {
			return false;
		}
		return true;
	};

	const getDriveFolders = (drive: OneDriveDrive): OneDriveRootItem[] =>
		(drive.rootItems || []).filter((item) => item.type === 'folder');

	const getDriveDisplayName = (drive: OneDriveDrive) => {
		if (drive.source === 'personal_drive') return 'OneDrive';
		if (drive.siteName) return `${drive.siteName} / ${drive.name}`;
		return drive.name;
	};

	const isLocationSelected = (drive: OneDriveDrive, folder: OneDriveRootItem | null) => {
		if (!oneDriveSelectedLocation) return false;
		if (oneDriveSelectedLocation.drive_id !== drive.id) return false;
		if (folder) {
			return oneDriveSelectedLocation.folder_id === folder.id;
		}
		return oneDriveSelectedLocation.folder_id == null;
	};

	const toggleDriveExpanded = (driveId: string) => {
		setExpandedDriveIds((prev) => ({
			...prev,
			[driveId]: !prev[driveId],
		}));
	};

	const handleOneDriveSourceChange = (source: OneDriveSourceType) => {
		setOneDriveSelectedSource(source);
		setExpandedDriveIds({});
	};

	const handleOneDriveReconnect = () => {
		resetOneDriveAuth();
		setExpandedDriveIds({});
		setParameterValues({});
		setApiSuccess(null);
		setApiError(null);
	};

	const renderOneDriveDirectory = (drives: OneDriveDrive[]) => {
		if (drives.length === 0) {
			return (
				<Alert severity="info" sx={{ marginTop: 1 }}>
					No drives available for this source.
				</Alert>
			);
		}

		return (
			<List
				dense
				sx={{
					marginTop: 1,
					border: '1px solid rgba(0, 0, 0, 0.12)',
					borderRadius: 1,
					maxHeight: 320,
					overflow: 'auto',
					backgroundColor: '#fff',
				}}
			>
				{drives.map((drive) => {
					const folders = getDriveFolders(drive);
					const expanded = !!expandedDriveIds[drive.id];
					const rootSelected = isLocationSelected(drive, null);

					return (
						<Box key={drive.id}>
							<ListItemButton
								selected={rootSelected}
								onClick={() => selectOneDriveRoot(drive)}
								sx={{
									'&.Mui-selected': {
										backgroundColor: 'rgba(68, 99, 136, 0.12)',
									},
								}}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									{rootSelected ? (
										<CheckCircleOutlineIcon fontSize="small" sx={{ color: '#446388' }} />
									) : (
										<FolderOpenOutlinedIcon fontSize="small" sx={{ color: '#446388' }} />
									)}
								</ListItemIcon>
								<ListItemText
									primary={getDriveDisplayName(drive)}
									secondary="Root folder"
									primaryTypographyProps={{ fontSize: 14 }}
									secondaryTypographyProps={{ fontSize: 12 }}
								/>
								{folders.length > 0 && (
									<Box
										component="span"
										onClick={(e) => {
											e.stopPropagation();
											toggleDriveExpanded(drive.id);
										}}
										sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
									>
										{expanded ? <ExpandLess /> : <ExpandMore />}
									</Box>
								)}
							</ListItemButton>

							<Collapse in={expanded} timeout="auto" unmountOnExit>
								<List dense component="div" disablePadding>
									{folders.map((folder) => {
										const folderSelected = isLocationSelected(drive, folder);
										return (
											<ListItemButton
												key={folder.id}
												selected={folderSelected}
												sx={{
													pl: 6,
													'&.Mui-selected': {
														backgroundColor: 'rgba(68, 99, 136, 0.12)',
													},
												}}
												onClick={() => selectOneDriveFolder(drive, folder)}
											>
												<ListItemIcon sx={{ minWidth: 36 }}>
													{folderSelected ? (
														<CheckCircleOutlineIcon
															fontSize="small"
															sx={{ color: '#446388' }}
														/>
													) : (
														<FolderOutlinedIcon fontSize="small" sx={{ color: '#446388' }} />
													)}
												</ListItemIcon>
												<ListItemText
													primary={folder.name}
													primaryTypographyProps={{ fontSize: 14 }}
												/>
											</ListItemButton>
										);
									})}
								</List>
							</Collapse>
						</Box>
					);
				})}
			</List>
		);
	};

	const confirmWorkplaceUnassign = () => {
		if (pendingWorkplaceId) {
			toggleWorkplaceAssignment(pendingWorkplaceId);
			const workplace = workplaces.find((w) => w.id === pendingWorkplaceId);
			setApiSuccess(`${workplace?.name} unassigned successfully!`);
			setTimeout(() => setApiSuccess(null), 2000);
		}
		setWorkplaceUnassignDialogOpen(false);
		setPendingWorkplaceId(null);
	};

	return (
		<div className={styles.createPartnerSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={backButtonClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button
								className={styles.saveButton}
								onClick={handleSubmit}
								disabled={formik.isSubmitting}
							>
								{formik.isSubmitting ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Create New Partner</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Partner Selection */}
				<Autocomplete
					options={commonPartners}
					getOptionLabel={(option) => option.label}
					loading={loadingCommonPartners}
					value={commonPartners.find((p) => p.id === Number(formik.values.partnerId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerId', newValue?.id || '');
						// Auto-fill label with the selected partner's label
						formik.setFieldValue('label', newValue?.label || '');

						// Reset Google OAuth when changing partners
						resetGoogleAuth();
						// Reset OneDrive OAuth when changing partners
						resetOneDriveAuth();
						setExpandedDriveIds({});
						resetToastFlow();

						// OAuth / Toast partners get parameters only after connect —
						// skip hardcoded template keys (ONE_DRIVE_CLIENT_ID, clientID, etc.)
						const label = newValue?.label?.toLowerCase() || '';
						const isGuidedPartner =
							label.includes('google drive') ||
							label.includes('onedrive') ||
							label.includes('one drive') ||
							label.includes('toast');

						if (isGuidedPartner || !newValue?.parameters) {
							setParameterValues({});
						} else if (Array.isArray(newValue.parameters) && newValue.parameters.length > 0) {
							const initialParams: Record<string, string> = {};
							const paramObj = newValue.parameters[0];
							if (typeof paramObj === 'object' && paramObj !== null) {
								Object.keys(paramObj).forEach((key) => {
									initialParams[key] = '';
								});
							}
							setParameterValues(initialParams);
						} else if (typeof newValue.parameters === 'object') {
							const initialParams: Record<string, string> = {};
							Object.keys(newValue.parameters).forEach((key) => {
								initialParams[key] = '';
							});
							setParameterValues(initialParams);
						} else {
							setParameterValues({});
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner *"
							margin="normal"
							error={formik.touched.partnerId && Boolean(formik.errors.partnerId)}
							helperText={formik.touched.partnerId && formik.errors.partnerId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingCommonPartners ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Partner Type Autocomplete */}
				<Autocomplete
					options={partnerTypes}
					getOptionLabel={(option) => option.label}
					loading={loadingPartnerTypes}
					value={partnerTypes.find((pt) => pt.id === Number(formik.values.partnerTypeId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerTypeId', newValue?.id || '');
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner Type *"
							margin="normal"
							error={formik.touched.partnerTypeId && Boolean(formik.errors.partnerTypeId)}
							helperText={formik.touched.partnerTypeId && formik.errors.partnerTypeId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingPartnerTypes ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Google Drive OAuth Integration */}
				{isGoogleDriveSelected() && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
							backgroundColor: isGoogleConnected
								? 'rgba(76, 175, 80, 0.05)'
								: 'rgba(0, 0, 0, 0.02)',
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 1,
							}}
						>
							Google Drive Integration
						</Box>

						<Box
							sx={{
								fontSize: '14px',
								color: 'rgba(0, 0, 0, 0.6)',
								marginBottom: 2,
							}}
						>
							Connect your Google Drive account to enable file integration capabilities.
						</Box>

						{/* OAuth Error Message */}
						{googleAuthError && !isGoogleConnected && (
							<Alert severity="error" sx={{ marginBottom: 2 }}>
								<AlertTitle>Authentication Error</AlertTitle>
								{googleAuthError}
							</Alert>
						)}

						{/* Connection Status */}
						{isGoogleConnected && (
							<Alert severity="success" sx={{ marginBottom: 2 }}>
								<AlertTitle>Connected Successfully</AlertTitle>
								Google Drive has been connected to this partner.
							</Alert>
						)}

						{/* Shared Drive Selection */}
						{isGoogleConnected && sharedDrives.length > 0 && (
							<Box sx={{ marginBottom: 2 }}>
								<Autocomplete
									options={sharedDrives}
									getOptionLabel={(option) => option.name}
									value={sharedDrives.find((d) => d.id === selectedDriveId) || null}
									onChange={(_, newValue) => {
										setSelectedDriveId(newValue?.id || null);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Shared Drive"
											margin="normal"
											helperText={
												sharedDrives.length === 1
													? 'Only one shared drive available (auto-selected)'
													: 'Select a Google Shared Drive to use with this partner'
											}
										/>
									)}
								/>
							</Box>
						)}

						{/* Drive Fetch Error */}
						{isGoogleConnected && googleAuthError && (
							<Alert severity="warning" sx={{ marginBottom: 2 }}>
								<AlertTitle>Shared Drives Access Limited</AlertTitle>
								{googleAuthError}
							</Alert>
						)}

						{/* Connect/Reconnect Button */}
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
							<Button
								onClick={initiateGoogleAuth}
								disabled={isGoogleAuthLoading}
								className={styles.googleDriveButton}
								style={{
									backgroundColor: isGoogleConnected ? '#4caf50' : '#446388',
									color: '#fff',
									padding: '10px 20px',
									fontSize: '14px',
									opacity: isGoogleAuthLoading ? 0.6 : 1,
								}}
							>
								{isGoogleAuthLoading ? (
									<>
										<CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
										Authenticating...
									</>
								) : isGoogleConnected ? (
									<>✓ Connected to Google Drive</>
								) : (
									'Connect Google Drive'
								)}
							</Button>

							{/* Reconnect Option */}
							{isGoogleConnected && (
								<Button
									onClick={() => {
										resetGoogleAuth();
										setParameterValues({});
										setApiSuccess(null);
										setApiError(null);
									}}
									className={styles.reconnectButton}
									style={{
										color: '#446388',
										border: '1px solid #446388',
										backgroundColor: 'transparent',
										padding: '8px 16px',
										fontSize: '14px',
									}}
								>
									Reconnect
								</Button>
							)}
						</Box>

						{/* Helper Text */}
						{!isGoogleConnected && (
							<Box
								sx={{
									fontSize: '12px',
									color: 'rgba(0, 0, 0, 0.6)',
									marginTop: 1,
								}}
							>
								Note: You must connect Google Drive before you can save this partner.
							</Box>
						)}
					</Box>
				)}

				{/* OneDrive OAuth Integration */}
				{isOneDriveSelected() && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
							backgroundColor: isOneDriveConnected
								? 'rgba(76, 175, 80, 0.05)'
								: 'rgba(0, 0, 0, 0.02)',
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 1,
							}}
						>
							Microsoft OneDrive Integration
						</Box>

						<Box
							sx={{
								fontSize: '14px',
								color: 'rgba(0, 0, 0, 0.6)',
								marginBottom: 2,
							}}
						>
							Connect your Microsoft OneDrive account, then choose Personal Drive or SharePoint
							and pick the folder where uploaded documents will be saved.
						</Box>

						{/* OAuth Error Message */}
						{oneDriveAuthError && !isOneDriveConnected && (
							<Alert severity="error" sx={{ marginBottom: 2 }}>
								<AlertTitle>Authentication Error</AlertTitle>
								{oneDriveAuthError}
							</Alert>
						)}

						{/* Connection Status */}
						{isOneDriveConnected && (
							<Alert severity="success" sx={{ marginBottom: 2 }}>
								<AlertTitle>Connected Successfully</AlertTitle>
								Microsoft OneDrive has been connected to this partner.
							</Alert>
						)}

						{/* Connect / Reconnect Button */}
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
							<Button
								onClick={initiateOneDriveAuth}
								disabled={isOneDriveAuthLoading}
								className={styles.googleDriveButton}
								style={{
									backgroundColor: isOneDriveConnected ? '#4caf50' : '#446388',
									color: '#fff',
									padding: '10px 20px',
									fontSize: '14px',
									opacity: isOneDriveAuthLoading ? 0.6 : 1,
								}}
							>
								{isOneDriveAuthLoading ? (
									<>
										<CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
										Authenticating...
									</>
								) : isOneDriveConnected ? (
									<>&#10003; Connected to Microsoft OneDrive</>
								) : (
									'Connect OneDrive'
								)}
							</Button>

							{isOneDriveConnected && (
								<Button
									onClick={handleOneDriveReconnect}
									className={styles.reconnectButton}
									style={{
										color: '#446388',
										border: '1px solid #446388',
										backgroundColor: 'transparent',
										padding: '8px 16px',
										fontSize: '14px',
									}}
								>
									Reconnect
								</Button>
							)}
						</Box>

						{/* Source + folder picker */}
						{isOneDriveConnected && (
							<Box sx={{ marginTop: 2 }}>
								<FormControl component="fieldset">
									<FormLabel
										component="legend"
										sx={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.7)', marginBottom: 1 }}
									>
										Choose storage source
									</FormLabel>
									<RadioGroup
										row
										value={oneDriveSelectedSource || ''}
										onChange={(e) =>
											handleOneDriveSourceChange(e.target.value as OneDriveSourceType)
										}
									>
										<FormControlLabel
											value="personal_drive"
											control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />}
											label="Personal Drive"
										/>
										<FormControlLabel
											value="sharepoint_site"
											control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />}
											label="SharePoint Site"
										/>
									</RadioGroup>
								</FormControl>

								{oneDriveSelectedSource === 'personal_drive' && (
									<Box sx={{ marginTop: 1 }}>
										<Box sx={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.6)', marginBottom: 0.5 }}>
											Select the OneDrive root or a subfolder
										</Box>
										{renderOneDriveDirectory(personalDrives)}
									</Box>
								)}

								{oneDriveSelectedSource === 'sharepoint_site' && (
									<Box sx={{ marginTop: 1 }}>
										<Box sx={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.6)', marginBottom: 0.5 }}>
											Select a SharePoint library root or a subfolder
										</Box>
										{renderOneDriveDirectory(sharePointDrives)}
									</Box>
								)}

								{oneDriveSelectedLocation && (
									<Alert severity="info" sx={{ marginTop: 2 }}>
										Selected:{' '}
										<strong>
											{oneDriveSelectedLocation.source === 'personal_drive'
												? 'Personal Drive'
												: 'SharePoint'}
										</strong>
										{' — '}
										{oneDriveSelectedLocation.drive_name}
										{oneDriveSelectedLocation.site_name
											? ` (${oneDriveSelectedLocation.site_name})`
											: ''}
										{oneDriveSelectedLocation.folder_name
											? ` / ${oneDriveSelectedLocation.folder_name}`
											: ' / (root)'}
									</Alert>
								)}

								{!oneDriveSelectedLocation && oneDriveSelectedSource && (
									<Box
										sx={{
											fontSize: '12px',
											color: 'rgba(0, 0, 0, 0.6)',
											marginTop: 1,
										}}
									>
										Select a root folder or subfolder before saving.
									</Box>
								)}
							</Box>
						)}

						{!isOneDriveConnected && (
							<Box
								sx={{
									fontSize: '12px',
									color: 'rgba(0, 0, 0, 0.6)',
									marginTop: 1,
								}}
							>
								Note: You must connect Microsoft OneDrive before you can save this partner.
							</Box>
						)}
					</Box>
				)}

				{/* Toast POS Integration */}
				{isToastSelected() && (
					<ToastConnectFlow
						key={toastFlowKey}
						onParametersChange={handleToastParametersChange}
						onReset={() => setIsToastRestaurantConnected(false)}
					/>
				)}

				{isToastSelected() && !isToastRestaurantConnected && (
					<Box
						sx={{
							fontSize: '12px',
							color: 'rgba(0, 0, 0, 0.6)',
							marginBottom: 1,
						}}
					>
						Note: You must confirm a Toast restaurant location before you can save this partner.
					</Box>
				)}

				{/* Dynamic Parameter Fields — only after OAuth succeeds for Google / OneDrive */}
				{shouldShowParameterFields() && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 2,
							}}
						>
							Partner Configuration Parameters
						</Box>
						{Object.keys(parameterValues).map((key) => {
							const keyLower = key.toLowerCase();
							const isGoogleOAuthField =
								isGoogleConnected &&
								(keyLower === 'encrypted_refresh_token' ||
									keyLower === 'token' ||
									keyLower === 'access_token' ||
									keyLower === 'shared_drive_id' ||
									keyLower === 'drive_id');
							const isOneDriveOAuthField =
								isOneDriveConnected &&
								[
									'encrypted_refresh_token',
									'source',
									'drive_id',
									'drive_name',
									'site_id',
									'site_name',
									'folder_id',
									'folder_name',
								].includes(keyLower);
							const isReadOnly = isGoogleOAuthField || isOneDriveOAuthField;

							let helperText: string | undefined;
							if (isGoogleOAuthField) {
								helperText = 'Auto-filled by Google Drive authentication';
							} else if (isOneDriveOAuthField) {
								helperText = 'Auto-filled by Microsoft OneDrive authentication';
							}

							return (
								<TextField
									key={key}
									label={key}
									value={parameterValues[key]}
									onChange={(e) => {
										if (isReadOnly) return;
										setParameterValues((prev) => ({
											...prev,
											[key]: e.target.value,
										}));
									}}
									margin="normal"
									fullWidth
									InputProps={{ readOnly: isReadOnly }}
									helperText={helperText}
								/>
							);
						})}
					</Box>
				)}

				{/* Partner Status */}
				<Box
					sx={{
						marginY: 2,
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.active}
								onChange={handleActivateToggle}
								sx={{
									color: '#446388',
									'&.Mui-checked': {
										color: '#446388',
									},
								}}
							/>
						}
						label={'Active Partner'}
					/>
				</Box>

				{/* Workplace Assignment - Only show if partner is active */}
				{formik.values.active && (
					<Box
						sx={{
							border: '1px solid #ccc',
							borderRadius: 2,
							padding: 2,
							marginY: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: 2,
							}}
						>
							<Box
								sx={{
									fontWeight: 400,
									fontSize: '16px',
									color: 'rgb(0 0 0 / 87%)',
								}}
							>
								Workplace Assignment
							</Box>
							{!loadingWorkplaces && workplaces.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={allWorkplacesSelected}
											indeterminate={someWorkplacesSelected}
											onChange={(e) => handleSelectAllWorkplaces(e.target.checked)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
												'&.MuiCheckbox-indeterminate': {
													color: '#446388',
												},
											}}
										/>
									}
									label="Select All"
								/>
							)}
						</Box>
						{loadingWorkplaces ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
								<CircularProgress size={24} color="inherit" />
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
								{workplaces.map((workplace) => (
									<Box
										key={workplace.id}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '4px 8px',
											borderRadius: 1,
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)',
											},
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span style={{ marginLeft: workplace.level * 20 }}>
												{workplace.level > 1 && '└─ '}
												{workplace.name}
											</span>
											{/* {workplace.selected && <Chip label="Assigned" size="small" color="primary" />} */}
										</Box>
										<Checkbox
											checked={workplace.selected}
											onChange={() => handleWorkplaceToggle(workplace.id)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
											}}
										/>
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}
			</form>

			{/* Partner Activation Confirmation Dialog */}
			<Dialog
				open={activateDialogOpen}
				onClose={() => setActivateDialogOpen(false)}
				aria-labelledby="activate-dialog-title"
				aria-describedby="activate-dialog-description"
			>
				<DialogTitle id="activate-dialog-title">Activate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="activate-dialog-description">
						Are you sure you want to activate this partner? This will enable integration
						capabilities.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setActivateDialogOpen(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={confirmActivatePartner} color="primary" autoFocus>
						Activate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Partner Deactivation Confirmation Dialog */}
			<Dialog
				open={deactivateDialogOpen}
				onClose={() => setDeactivateDialogOpen(false)}
				aria-labelledby="deactivate-dialog-title"
				aria-describedby="deactivate-dialog-description"
			>
				<DialogTitle id="deactivate-dialog-title">Deactivate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="deactivate-dialog-description">
						Are you sure you want to deactivate this partner? This will disable all integration
						capabilities and remove all workplace assignments.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeactivateDialogOpen(false)}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmDeactivatePartner}
						color="error"
						autoFocus
						className={styles.dialogBackButton}
					>
						Deactivate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Workplace Unassignment Confirmation Dialog */}
			<Dialog
				open={workplaceUnassignDialogOpen}
				onClose={() => {
					setWorkplaceUnassignDialogOpen(false);
					setPendingWorkplaceId(null);
				}}
				aria-labelledby="workplace-unassign-dialog-title"
				aria-describedby="workplace-unassign-dialog-description"
			>
				<DialogTitle id="workplace-unassign-dialog-title">Unassign Workplace</DialogTitle>
				<DialogContent>
					<DialogContentText id="workplace-unassign-dialog-description">
						Are you sure you want to unassign &quot;
						{workplaces.find((w) => w.id === pendingWorkplaceId)?.name}&quot; from this partner?
						This will remove the partner&apos;s access to this workplace.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setWorkplaceUnassignDialogOpen(false);
							setPendingWorkplaceId(null);
						}}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmWorkplaceUnassign}
						color="warning"
						autoFocus
						className={styles.dialogBackButton}
					>
						Unassign Workplace
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default CreatePartnerSettings;
